import {useState} from "react";
import {archiveVacancy, postVacancyToHH, restoreVacancy, syncVacancyFromHH, syncVacancyToHH} from "../../services/vacancyApi";
import {runVacancyFilterNow, formatFilterRunSummary} from "../../services/vacancyFilterApi";
import {useAlertContext} from "../../context/AlertContext";
import {useHHDictionaries} from "../../hooks/useHHDictionaries";
import {Link} from "react-router-dom";
import {
    scheduleLabel,
    employmentLabel,
} from "../../utils/hhLabels";

export default function VacancyList({
    vacancies,
    onVacancyDeleted,
    onVacancyEdit,
    onVacancyUpdated,
    onOpenHHNegotiations,
    onManageFilter,
    canColdSearch = false,
}) {
    const {showAlert} = useAlertContext();
    const {dictionaries} = useHHDictionaries();
    const [publishing, setPublishing] = useState({});
    const [syncing, setSyncing] = useState({});
    const [templating, setTemplating] = useState({});
    const [runningFilter, setRunningFilter] = useState({});
    const [changingArchive, setChangingArchive] = useState({});
    const [syncMenuOpen, setSyncMenuOpen] = useState(null);

    const handleArchiveChange = async (vacancy) => {
        const id = Number(vacancy?.id);
        if (!Number.isFinite(id)) return;
        const restoring = Boolean(vacancy.archived_at);
        const question = restoring
            ? "Вернуть вакансию из архива? Для вакансии с HH.ru будет создана новая публикация."
            : "Перенести вакансию в архив? Связанная публикация на HH.ru тоже будет архивирована.";
        if (!window.confirm(question)) return;
        try {
            setChangingArchive((prev) => ({ ...prev, [id]: true }));
            const updated = restoring ? await restoreVacancy(id) : await archiveVacancy(id);
            onVacancyUpdated?.({ ...vacancy, ...updated });
            showAlert(restoring ? "Вакансия восстановлена" : "Вакансия перенесена в архив", "success");
        } catch (error) {
            showAlert(`Не удалось изменить статус вакансии: ${error?.message || error}`, "error");
        } finally {
            setChangingArchive((prev) => ({ ...prev, [id]: false }));
        }
    };

    const handleRunFilter = async (vacancy) => {
        const id = Number(vacancy?.id);
        if (!Number.isFinite(id)) return;
        if (!vacancy?.filter_id) {
            showAlert("Сначала привяжите фильтр к вакансии", "warning");
            return;
        }
        if (!vacancy?.hh_vacancy_id && !vacancy?.hh_vacancy_url) {
            showAlert("Сначала опубликуйте или привяжите вакансию к HH.ru", "warning");
            return;
        }
        try {
            setRunningFilter((prev) => ({ ...prev, [id]: true }));
            showAlert("Прогоняем фильтр по откликам HH (порция отказов)…", "info");
            const result = await runVacancyFilterNow(id);
            const { msg, level } = formatFilterRunSummary(result?.summary || result || {});
            showAlert(msg, level);
        } catch (e) {
            showAlert(`Не удалось прогнать фильтр: ${e.message || e}`, "error");
        } finally {
            setRunningFilter((prev) => ({ ...prev, [id]: false }));
        }
    };

    const handlePublishToHH = async (vacancy) => {
        const id = Number(vacancy?.id);

        if (!Number.isFinite(id)) {
            showAlert("Некорректный vacancy.id — не могу опубликовать", "error");
            return;
        }

        try {
            setPublishing(prev => ({ ...prev, [id]: true }));
            showAlert("Публикуем вакансию...", "info");

            const published = await postVacancyToHH(id, {});
            const updated = {
                ...vacancy,
                hh_vacancy_id: published?.hh_vacancy_id ?? vacancy.hh_vacancy_id,
                hh_vacancy_url: published?.alternate_url ?? published?.hh_vacancy_url ?? vacancy.hh_vacancy_url,
            };
            onVacancyUpdated?.(updated);
            showAlert("Вакансия успешно опубликована на HH.ru!", "success");
        } catch (err) {
            const msg = err?.message || "Неизвестная ошибка";
            showAlert(`Ошибка публикации: ${msg}`, "error");
            console.error("HH publish error:", err);
        } finally {
            setPublishing(prev => ({ ...prev, [id]: false }));
        }
    };

    const handleSyncToHH = async (vacancy) => {
        const id = Number(vacancy?.id);
        if (!Number.isFinite(id)) {
            showAlert("Некорректный vacancy.id — не могу обновить на HH", "error");
            return;
        }
        try {
            setSyncing((prev) => ({ ...prev, [id]: true }));
            showAlert("Обновляем вакансию на HH.ru...", "info");
            const synced = await syncVacancyToHH(id);
            onVacancyUpdated?.({
                ...vacancy,
                hh_vacancy_id: synced?.hh_vacancy_id ?? vacancy.hh_vacancy_id,
                hh_vacancy_url:
                    synced?.alternate_url ?? synced?.hh_vacancy_url ?? vacancy.hh_vacancy_url,
            });
            showAlert("Вакансия обновлена на HH.ru", "success");
        } catch (err) {
            showAlert(`Не удалось обновить на HH.ru: ${err?.message || err}`, "error");
            console.error("HH sync error:", err);
        } finally {
            setSyncing((prev) => ({ ...prev, [id]: false }));
            setSyncMenuOpen(null);
        }
    };

    const handleSyncFromHH = async (vacancy) => {
        const id = Number(vacancy?.id);
        if (!Number.isFinite(id)) {
            showAlert("Некорректный vacancy.id — не могу получить данные с HH.ru", "error");
            return;
        }
        try {
            setSyncing((prev) => ({ ...prev, [id]: true }));
            showAlert("Получаем актуальные данные вакансии с HH.ru…", "info");
            const result = await syncVacancyFromHH(id);
            const updated = result?.vacancy;
            if (updated) onVacancyUpdated?.({ ...vacancy, ...updated });
            showAlert(
                result?.hh_archived
                    ? "Данные получены. Публикация на HH.ru уже в архиве"
                    : "Данные вакансии обновлены с HH.ru",
                "success"
            );
        } catch (err) {
            showAlert(`Не удалось получить данные с HH.ru: ${err?.message || err}`, "error");
            console.error("HH pull sync error:", err);
        } finally {
            setSyncing((prev) => ({ ...prev, [id]: false }));
            setSyncMenuOpen(null);
        }
    };

    const handleMakeTemplate = async (vacancy) => {
        const id = Number(vacancy?.id);
        if (!Number.isFinite(id)) return;
        try {
            setTemplating((prev) => ({ ...prev, [id]: true }));
            const { makeVacancyTemplate } = await import("../../services/vacancyApi");
            const cloned = await makeVacancyTemplate(id);
            onVacancyUpdated?.(cloned);
            showAlert(`Шаблон создан: ${cloned.public_code || cloned.name}`, "success");
        } catch (err) {
            showAlert(`Не удалось создать шаблон: ${err.message || err}`, "error");
        } finally {
            setTemplating((prev) => ({ ...prev, [id]: false }));
        }
    };

    if(!vacancies || vacancies.length === 0) {
        return (
            <div className="bg-white p-12 rounded-2xl shadow-sm border border-slate-200/60 text-center flex flex-col items-center">
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 text-slate-400">
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                </div>
                <h3 className="text-lg font-medium text-slate-800">Нет вакансий</h3>
                <p className="text-slate-500 mt-1">Добавьте новую вакансию, чтобы начать работу.</p>
            </div>
        );
    }

    const cardTone = (vacancy) => {
        if (vacancy.deadline_state === "overdue") return "bg-red-50/50 border-red-200";
        if (vacancy.deadline_state === "warning") return "bg-yellow-50/50 border-yellow-200";
        if (vacancy.is_template) return "bg-slate-50/50 border-slate-200 border-dashed";
        return "bg-white border-slate-200/60";
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {vacancies.map(vacancy => {
                const scheduleId = vacancy.schedule_id || vacancy.work_format;
                const employmentId = vacancy.employment_id || vacancy.employment_type;
                const scheduleText = scheduleLabel(scheduleId, dictionaries);
                const employmentText = employmentLabel(employmentId, dictionaries);

                return (
                <div key={vacancy.id} className={`${cardTone(vacancy)} rounded-2xl shadow-sm border hover:shadow-md transition-all duration-300 flex flex-col`}>
                    <div className="p-6 flex-1">
                        <div className="flex justify-between items-start mb-4">
                            <div className="flex-1 pr-4">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <span className="text-xs font-semibold text-slate-400 tracking-wide uppercase">
                                        {vacancy.public_code || `В-${vacancy.id}`}
                                    </span>
                                    {vacancy.is_template && (
                                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-medium tracking-wide uppercase border border-slate-200">
                                            Шаблон
                                        </span>
                                    )}
                                    {vacancy.is_internal_hidden && (
                                        <span className="ml-2 px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold uppercase">
                                            Скрыта внутри
                                        </span>
                                    )}
                                    {vacancy.archived_at && (
                                        <span className="ml-2 rounded-full bg-slate-700 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                                            Архив
                                        </span>
                                    )}
                                    {vacancy.filter_id ? (
                                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-medium tracking-wide uppercase border border-blue-100">
                                            Фильтр #{vacancy.filter_id}
                                        </span>
                                    ) : null}
                                    {vacancy.deadline_state === "overdue" && (
                                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold tracking-wide uppercase border border-red-200">
                                            Просрочено
                                        </span>
                                    )}
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 leading-tight">{vacancy.name}</h3>
                            </div>
                            <span className="shrink-0 px-2.5 py-1 bg-yellow-50 text-yellow-800 border border-yellow-100 rounded-lg text-xs font-medium">
                                {vacancy.department || "Без отдела"}
                            </span>
                        </div>

                        {vacancy.work_address ? (
                            <p className="mb-3 text-xs text-slate-500">Адрес: {vacancy.work_address}</p>
                        ) : null}

                        <div className="space-y-2.5 text-sm mb-5 text-slate-600">
                            {(vacancy.city || vacancy.region) && (
                                <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                    <span className="truncate">
                                        {[vacancy.city, vacancy.region].filter(Boolean).join(", ")}
                                    </span>
                                </div>
                            )}

                            {(scheduleText || employmentText) && (
                                <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    <span className="truncate">{[scheduleText, employmentText].filter(Boolean).join(" · ")}</span>
                                </div>
                            )}

                            {(vacancy.salary_from > 0 || vacancy.salary_to > 0) && (
                                <div className="flex items-center gap-2 font-medium text-slate-900">
                                    <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    <span>
                                        {vacancy.salary_from > 0 && vacancy.salary_from.toLocaleString("ru-RU")}
                                        {vacancy.salary_from > 0 && vacancy.salary_to > 0 && " – "}
                                        {vacancy.salary_to > 0 && vacancy.salary_to.toLocaleString("ru-RU")}
                                        {" ₽"}
                                    </span>
                                </div>
                            )}
                        </div>

                        {Array.isArray(vacancy.required_hard_skills) && vacancy.required_hard_skills.length > 0 && (
                            <div className="mb-4">
                                <div className="flex flex-wrap gap-1.5">
                                    {vacancy.required_hard_skills.slice(0, 4).map((skill, idx) => (
                                        <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-600 rounded border border-slate-200/60 text-xs font-medium">{skill}</span>
                                    ))}
                                    {vacancy.required_hard_skills.length > 4 && (
                                        <span className="px-2 py-1 bg-slate-50 text-slate-500 rounded border border-slate-200/60 text-xs font-medium">
                                            +{vacancy.required_hard_skills.length - 4}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        <div className="flex gap-2 flex-wrap">
                            <Link
                                to={`/vacancies/${encodeURIComponent(vacancy.id)}/tests`}
                                state={{ vacancyName: vacancy.name }}
                                data-testid={`vacancy-tests-btn-${vacancy.id}`}
                                className="text-sm px-3 py-1.5 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg transition-colors font-medium border border-transparent hover:border-slate-300"
                                title="Открыть тесты вакансии на отдельной странице"
                            >
                                Тесты{Number(vacancy.test_count) > 0 ? ` · ${vacancy.test_count}` : ""}
                            </Link>
                            {onManageFilter && (
                                <button
                                    type="button"
                                    data-testid={`vacancy-filter-btn-${vacancy.id}`}
                                    onClick={() => onManageFilter(vacancy)}
                                    className="text-sm px-3 py-1.5 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg transition-colors font-medium border border-transparent hover:border-slate-300"
                                >
                                    {vacancy.filter_id ? "Фильтр" : "Добавить фильтр"}
                                </button>
                            )}
                            {vacancy.filter_id && (vacancy.hh_vacancy_id || vacancy.hh_vacancy_url) ? (
                                <button
                                    type="button"
                                    data-testid={`vacancy-run-filter-${vacancy.id}`}
                                    onClick={() => handleRunFilter(vacancy)}
                                    disabled={Boolean(runningFilter[vacancy.id])}
                                    title="Отказать порцией откликов на HH, которые не проходят фильтр (лимит, чтобы не нагружать API)"
                                    className="text-sm px-3 py-1.5 bg-[#cda834]/15 text-slate-800 hover:bg-[#cda834]/25 rounded-lg transition-colors font-medium border border-[#cda834]/40 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {runningFilter[vacancy.id] ? "Прогон…" : "Прогнать фильтр"}
                                </button>
                            ) : null}
                            <button 
                                onClick={() => handleMakeTemplate(vacancy)}
                                disabled={templating[vacancy.id] || vacancy.is_template}
                                className="text-sm px-3 py-1.5 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg transition-colors font-medium border border-transparent hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {templating[vacancy.id] ? "..." : "В шаблон"}
                            </button>
                        </div>
                    </div>

                    <div className="p-4 border-t border-slate-100 bg-slate-50/70 rounded-b-2xl">
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={() => onVacancyEdit(vacancy)}
                                className="inline-flex min-h-10 items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50"
                            >
                                <svg aria-hidden="true" className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.862 3.487a2.1 2.1 0 113 2.97L8.4 18.03 4 19l1.036-4.32L16.862 3.487z" /></svg>
                                Изменить
                            </button>

                            {!vacancy.archived_at && (vacancy.hh_vacancy_id || vacancy.hh_vacancy_url) ? (
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setSyncMenuOpen((current) => current === vacancy.id ? null : vacancy.id)}
                                        disabled={syncing[vacancy.id]}
                                        aria-expanded={syncMenuOpen === vacancy.id}
                                        data-testid={`vacancy-sync-menu-${vacancy.id}`}
                                        className="inline-flex min-h-10 w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-amber-300 bg-white px-3 py-2 text-sm font-medium text-amber-900 transition-colors hover:border-amber-400 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <span aria-hidden="true" className="inline-flex h-5 min-w-5 items-center justify-center rounded-md bg-amber-100 px-1 text-[10px] font-bold leading-none text-amber-900">hh</span>
                                        {syncing[vacancy.id] ? "Синхронизация…" : "Синхронизация"}
                                        <span aria-hidden="true" className="text-xs">▾</span>
                                    </button>
                                    {syncMenuOpen === vacancy.id ? (
                                        <div className="absolute right-0 z-20 mt-1 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white p-1 shadow-xl">
                                            <button
                                                type="button"
                                                onClick={() => handleSyncToHH(vacancy)}
                                                data-testid={`vacancy-sync-to-hh-${vacancy.id}`}
                                                className="w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-amber-50"
                                            >
                                                <span className="block font-semibold">Передать изменения на HH.ru</span>
                                                <span className="block text-xs text-slate-500">Обновить публикацию данными платформы</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleSyncFromHH(vacancy)}
                                                data-testid={`vacancy-sync-from-hh-${vacancy.id}`}
                                                className="w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-amber-50"
                                            >
                                                <span className="block font-semibold">Получить данные с HH.ru</span>
                                                <span className="block text-xs text-slate-500">Обновить карточку вакансии на платформе</span>
                                            </button>
                                        </div>
                                    ) : null}
                                </div>
                            ) : !vacancy.archived_at ? (
                                <button
                                    type="button"
                                    onClick={() => handlePublishToHH(vacancy)}
                                    disabled={publishing[vacancy.id]}
                                    className="inline-flex min-h-10 items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-amber-300 bg-white px-3 py-2 text-sm font-medium text-amber-900 transition-colors hover:border-amber-400 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <span aria-hidden="true" className="inline-flex h-5 min-w-5 items-center justify-center rounded-md bg-amber-100 px-1 text-[10px] font-bold leading-none text-amber-900">hh</span>
                                    {publishing[vacancy.id] ? "Публикуем…" : "Опубликовать"}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => handleArchiveChange(vacancy)}
                                    disabled={changingArchive[vacancy.id]}
                                    className="inline-flex min-h-10 items-center justify-center rounded-xl border border-emerald-300 bg-white px-3 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
                                >
                                    {changingArchive[vacancy.id] ? "Восстанавливаем…" : "Вернуть из архива"}
                                </button>
                            )}
                        </div>

                        <div className="mt-2 flex items-center gap-2">

                            {canColdSearch && vacancy.hh_vacancy_id ? (
                                <Link
                                    to={`/vacancies/${vacancy.id}/cold-search`}
                                    className="inline-flex min-h-10 min-w-0 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-slate-200 bg-white px-3 py-2 text-center text-sm font-medium text-slate-700 transition-colors hover:border-amber-300 hover:bg-amber-50 hover:text-amber-950"
                                >
                                    <svg aria-hidden="true" className="h-4 w-4 shrink-0 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m1.35-5.65a7 7 0 11-14 0 7 7 0 0114 0zM11 8v6m-3-3h6" /></svg>
                                    Холодный подбор
                                </Link>
                            ) : null}

                            {(vacancy.hh_vacancy_id || vacancy.hh_vacancy_url) ? (
                                <a
                                    href={vacancy.hh_vacancy_url || `https://hh.ru/vacancy/${vacancy.hh_vacancy_id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-emerald-200 bg-white text-emerald-700 transition-colors hover:border-emerald-300 hover:bg-emerald-50"
                                    title="Открыть вакансию на HH.ru"
                                    aria-label="Открыть вакансию на HH.ru"
                                >
                                    <svg aria-hidden="true" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                </a>
                            ) : null}

                            {(() => {
                                const hhId = vacancy.hh_vacancy_id || (String(vacancy.hh_vacancy_url || "").match(/\/vacancy\/(\d+)/) || [])[1];
                                if (!hhId || !onOpenHHNegotiations) return null;
                                return (
                                    <button
                                        type="button"
                                        onClick={() => onOpenHHNegotiations({ ...vacancy, hh_vacancy_id: String(hhId) })}
                                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition-colors hover:border-amber-300 hover:bg-amber-50 hover:text-amber-800"
                                        title="Отклики кандидатов"
                                        aria-label="Отклики кандидатов"
                                    >
                                        <svg aria-hidden="true" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" /></svg>
                                    </button>
                                );
                            })()}

                            {!vacancy.archived_at ? (
                                <button
                                    type="button"
                                    onClick={() => handleArchiveChange(vacancy)}
                                    disabled={changingArchive[vacancy.id]}
                                    className="ml-auto inline-flex h-10 items-center justify-center rounded-xl border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                                    title="Перенести вакансию в архив"
                                >
                                    {changingArchive[vacancy.id] ? "…" : "В архив"}
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>
                )
            })}

        </div>
    );
}
